import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  Clock,
  Droplet,
  Sparkles,
  Thermometer,
  Waves,
  Zap,
} from "lucide-react";

import { usePendingActionsForGrow } from "@/lib/api/pending-actions";
import { useLatestReading } from "@/lib/api/grow-readings";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ReadingCaptureCard } from "@/components/grows/reading-capture-card";
import { GrowDiagnosePanel } from "@/pages/grows/diagnose";
import {
  latestReservoirState,
  useRefillSessions,
} from "@/lib/api/refill-sessions";
import { usePreferences } from "@/lib/preferences";
import {
  ecFromMsCm,
  ecUnitLabel,
  tempFromCelsius,
  tempUnitLabel,
} from "@/lib/units";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Metric } from "@/components/metric";
import { UrgencyBadge } from "@/components/urgency-badge";
import type { Grow, PendingAction, Urgency } from "@/lib/api/types";

import { GrowHistoryChart } from "./grow-history-chart";

// GrowCard is the dashboard's per-grow tile: current reservoir chemistry with
// deltas from the grow's ideal band, the next prioritized action, time since
// the last refill, and a sparkline of refill history. It pulls the latest
// refill session (sessions sort newest-first) to source the "current" reading
// and the pending-action list to source the next action.

const URGENCY_RANK: Record<Urgency, number> = {
  HIGH: 0,
  MED: 1,
  ROUTINE: 2,
  PARKED: 3,
};

export function GrowCard({ grow }: { grow: Grow }) {
  const { ecUnit, temperatureUnit } = usePreferences();
  const sessions = useRefillSessions({ grow_id: grow.id, limit: 20 });
  const pending = usePendingActionsForGrow(grow.id);
  const latestReadingQ = useLatestReading(grow.id);
  const [measuring, setMeasuring] = useState(false);

  const latest = sessions.data?.items[0];
  const state = latestReservoirState(latest);
  const lastRefillAt = latest?.closed_at ?? latest?.started_at;

  // Show the most recent of {latest saved reading, latest refill state} so a
  // pen reading saved from the grow page updates the dashboard tile too. Level
  // prefers the current reading when it recorded one, else the latest refill.
  const reading = latestReadingQ.data ?? null;
  const readingTime = reading ? Date.parse(reading.captured_at) : -Infinity;
  const stateTime = state?.captured_at
    ? Date.parse(state.captured_at)
    : lastRefillAt
      ? Date.parse(lastRefillAt)
      : -Infinity;
  const useReading = !!reading && readingTime >= stateTime;
  // Per-field precedence: a newer reading wins per metric, but one it left blank
  // (level-only / notes-only readings are possible) falls back to the refill
  // state instead of showing "—".
  const cur = {
    ph: useReading && reading!.ph != null ? reading!.ph : state?.ph,
    ec:
      useReading && reading!.ec_ms_per_cm != null
        ? reading!.ec_ms_per_cm
        : state?.ec,
    temp_c:
      useReading && reading!.temp_c != null ? reading!.temp_c : state?.temp_c,
    level_gal:
      useReading && reading!.level_gal != null ? reading!.level_gal : state?.level_gal,
  };
  const lastAt = useReading ? reading!.captured_at : lastRefillAt;

  const nextAction = useMemo(
    () =>
      (pending.data?.items ?? [])
        .slice()
        .sort((a, b) => URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency])[0],
    [pending.data?.items],
  );

  return (
    <Card className="flex flex-col">
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div className="min-w-0 space-y-0.5">
          <Link
            to="/grows/$growId"
            params={{ growId: grow.id }}
            className="block truncate text-base font-semibold hover:underline"
          >
            {grow.species}
          </Link>
          <p className="text-xs text-muted-foreground">
            {grow.system_type} · {grow.count} plant{grow.count === 1 ? "" : "s"} ·{" "}
            <span className="font-medium text-foreground/80">
              {grow.current_state}
            </span>
          </p>
        </div>
        <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
          <Clock className="size-3" aria-hidden />
          {lastAt
            ? formatDistanceToNow(new Date(lastAt), { addSuffix: true })
            : "no readings"}
        </span>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <Metric
            icon={<Droplet className="size-4" aria-hidden />}
            label="pH"
            current={cur.ph}
            ideal={grow.ph_ideal}
            min={grow.ph_min}
            max={grow.ph_max}
            digits={2}
          />
          <Metric
            icon={<Zap className="size-4" aria-hidden />}
            label={ecUnitLabel(ecUnit)}
            current={cur.ec}
            ideal={grow.ec_ideal}
            min={grow.ec_min}
            max={grow.ec_max}
            display={(n) => ecFromMsCm(n, ecUnit)}
            digits={ecUnit === "ppm700" ? 0 : 2}
          />
          <Metric
            icon={<Thermometer className="size-4" aria-hidden />}
            label={`Temp ${tempUnitLabel(temperatureUnit)}`}
            current={cur.temp_c}
            ideal={grow.temp_ideal_c}
            min={grow.temp_min_c}
            max={grow.temp_max_c}
            display={(n) => tempFromCelsius(n, temperatureUnit)}
            digits={1}
          />
          <Metric
            icon={<Waves className="size-4" aria-hidden />}
            label="Level"
            current={cur.level_gal}
            suffix=" gal"
            digits={1}
          />
        </div>

        <NextAction
          action={nextAction}
          loading={pending.isLoading}
          growID={grow.id}
        />

        {/* Quick reading capture (snapshot the live pen reading or type one and
            save it) + a separate Analyze action (opens the AI panel in a dialog),
            both straight from the dashboard. */}
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMeasuring((v) => !v)}
              aria-expanded={measuring}
            >
              {measuring ? "Hide reading" : "Take reading"}
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Sparkles className="size-4" aria-hidden />
                  Analyze
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Analyze {grow.species}</DialogTitle>
                  <DialogDescription>
                    Run an AI assessment of this grow against its targets and the
                    latest saved reading.
                  </DialogDescription>
                </DialogHeader>
                <GrowDiagnosePanel grow={grow} />
              </DialogContent>
            </Dialog>
          </div>
          {measuring && <ReadingCaptureCard grow={grow} />}
        </div>

        <div className="mt-auto">
          <GrowHistoryChart sessions={sessions.data?.items ?? []} />
        </div>
      </CardContent>
    </Card>
  );
}

function NextAction({
  action,
  loading,
  growID,
}: {
  action: PendingAction | undefined;
  loading: boolean;
  growID: string;
}) {
  if (loading) {
    return <p className="text-xs text-muted-foreground">Loading actions…</p>;
  }
  if (!action) {
    return (
      <p className="text-xs text-muted-foreground">No pending actions.</p>
    );
  }
  return (
    <Link
      to="/pending-actions"
      search={{ grow: growID }}
      className="flex items-start gap-2 rounded-md border border-dashed px-2.5 py-2 text-sm transition-colors hover:bg-accent/50"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <UrgencyBadge urgency={action.urgency} className="shrink-0" />
          <span className="truncate font-medium">{action.title}</span>
        </span>
        {action.rationale && (
          <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
            {action.rationale}
          </span>
        )}
      </span>
    </Link>
  );
}
